import React, { useEffect, useRef, useState } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import Button from '@/Shared/Button'
import Heading from '@/Shared/Forms/Heading'
import RichTextEditor from '@/Shared/RichTextEditor/Index'
import SlideOver from '@/Shared/SlideOver'
import TextInput from '@/Shared/Forms/TextInput'

export default function ProInvitation({ contact, open, onClosed }) {
  let focusRef = useRef()
  const { user } = usePage().props.auth

  const { clearErrors, data, errors, setData, post, processing, reset } = useForm({
    id: contact?.id || null,
    sender_contact_id: contact?.sender_contact_id,
    sender_contact_type: contact?.sender_contact_type || null,
    first_name: contact?.first_name || '',
    last_name: contact?.last_name || '',
    email: contact?.email || '',
    message: contact?.message || '',
  })

  const defaultMsg =
    contact?.message ||
    `
      <p id="greeting">Hi <span data-type="variable" class="px-1 bg-gray-200 border border-gray-300 rounded-md text-gray-600 uppercase font-medium" data-label="First Name" contenteditable="false" draggable="true"></span>,</p>
      <p>I'd like you to join our LSNPros.com referral system by creating your personal PRO-website.  This is a free service that provides you with tremendous exposure and benefits:</p>
      <ul>
      <li>FREE Placement in the internal Better Living Real Estate® "Find a PRO" referral Directory.</li>
      <li>FREE Placement in the public LSNPros.com PRO directory.</li>
      <li>FREE Placement in both Google.com and Bing.com search engines with a link back to your company. This boosts both your PRO-website and company website ranking in Google & Bing, the two most trafficked search sites on the internet!</li>
      </ul>
      <p>I'm happy to assist you with becoming part of LSNPros and our Better Living Real Estate® referral program.  Let me know if you have any questions.</p>
      <p>- ${user.first_name}</p>
    `
  const [message, setMessage] = useState(defaultMsg)

  useEffect(() => {
    if (message != data.message) {
      setData({ ...data, message: message })
    }
  }, [message])

  useEffect(() => contact && setMessage(contact?.message), [contact])

  const onClosing = () => {
    onClosed()

    setTimeout(() => {
      clearErrors()
      reset()

      setMessage(defaultMsg)
    }, 300)
  }

  const onFirstNameChanged = (value) => {
    setData({ ...data, first_name: value })

    setMessage(updateGreetingInHTML(value))
  }

  const updateGreetingInHTML = (first_name) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(message, 'text/html')

    const greetingElement = doc.querySelector('p#greeting')
    if (greetingElement) {
      greetingElement.innerHTML = first_name ? `Hi ${first_name},` : `Hi,`
    }

    return doc.body.innerHTML
  }

  const submit = (event) => {
    event.preventDefault()

    const endpoint = data.id ? route('pros.invitations.resend', data.id) : route('pros.store')

    post(endpoint, {
      onSuccess: () => {
        onClosing()
      },
    })
  }

  return (
    <SlideOver
      focusRef={focusRef}
      footerActions={
        <Button type="submit" theme="solid" form="newChecklist" disabled={processing}>
          <i className="fas fa-envelope mr-2 text-lg"></i>
          Send Invitation Email
        </Button>
      }
      title="Invite a PRO"
      size="max-w-2xl"
      show={open}
      keyboardClose={false}
      overlayClose={false}
      onClosed={onClosing}
    >
      <form id="newChecklist" className="mb-5 grid gap-x-6 xl:grid-cols-2" onSubmit={submit}>
        <TextInput
          ref={focusRef}
          label="First name"
          name="first_name"
          value={data.first_name}
          error={errors.first_name}
          onChange={(value) => onFirstNameChanged(value)}
          required
          disabled={contact != undefined}
        />

        <TextInput
          label="Last name"
          name="last_name"
          value={data.last_name}
          error={errors.last_name}
          onChange={(value) => setData({ ...data, last_name: value })}
          required
          disabled={contact != undefined}
        />

        <div className="col-span-2">
          <TextInput
            label="Email"
            name="email"
            value={data.email}
            error={errors.email}
            onChange={(value) => setData({ ...data, email: value })}
            required
            disabled={contact != undefined}
          />
        </div>

        <div className="col-span-2 mb-5">
          <Heading>Invitation Message</Heading>
          <p>
            The following is the message that will be sent to this PRO. A <i>suggested</i> personal message has been provided for you.
          </p>

          <div className="mt-3 space-y-5 border border-gray-300 bg-gray-100 p-6">
            <p>
              {user.full_name} is inviting you to join <b>LSNPros.com</b> to provide you with more referrals and exposure on search engines.
            </p>

            <p className="text-lg font-bold">Personal Message from {user.first_name}:</p>

            <RichTextEditor
              theme="simple"
              value={message}
              height="lg:overflow-y-auto min-h-[150px] max-h-[500px]"
              onChange={(value) => setMessage(value)}
            />

            <p>
              <a className="font-bold text-blue-500 underline" href={user.url} target="_blank">
                Contact {user.first_name}
              </a>{' '}
              to learn more about this local professional services community.
            </p>
          </div>
        </div>
      </form>
    </SlideOver>
  )
}
